// src/services/datev/datevPendingTaskService.ts
//
// Frontend-Service zum idempotenten Anlegen von Pending Tasks in der
// datev_pending_tasks-Tabelle. Wird aus useDeadlines.handleFileUpload
// (Stufe 2: invoice_draft_autofill) aufgerufen.
//
// Die Idempotenz-Logik ist identisch zum Backend-Pendant in
// netlify/functions/_shared/datevPendingTasks.js (createTask):
// Pro (customer_id, year, month, task_type) darf nur ein aktiver Task
// (status pending/partially_fulfilled) existieren.
//
// FÄLLIGKEITSQUELLE
// Ein Task wartet entweder auf ein Dokument im DATEV-Spiegel ('datev_spiegel',
// Standard) oder auf ein Ereignis außerhalb von DATEV ('extern'), etwa das
// Abschluss-Manifest eines externen Laufs. Nur im Spiegel-Fall ist mindestens
// ein benötigtes Dokument zwingend: ohne diese Sperre entstünden Tasks, deren
// Bedingung nie erfüllt wird und die zwangsläufig eskalieren.
//
// Der Wert steuert ausschließlich diese Plausibilitätsprüfung und wird nicht
// gespeichert. Welche Bedingung tatsächlich gilt, entscheidet allein die Engine
// in supabase/functions/_shared/datev/pending-tasks.ts anhand des Task-Typs.
// Damit bleibt die Fälligkeit an genau einer Stelle definiert, und der Service
// muss keine Liste von Task-Typen kennen.
import { supabase } from '../../shared/services/supabase';
export type PendingTaskFaelligkeitsQuelle = 'datev_spiegel' | 'extern';
export interface CreatePendingTaskInput {
  customerId: string;
  year: number;
  month: number;
  taskType: string;
  requiredDocuments?: string[];
  requiredNextMonthDocuments?: string[];
  /**
   * Woher die Fälligkeit dieses Tasks kommt. Standard ist der DATEV-Spiegel;
   * 'extern' kennzeichnet Tasks, deren Auslöser außerhalb von DATEV liegt und
   * die deshalb bewusst ohne Dokumentanker angelegt werden.
   */
  faelligkeitsQuelle?: PendingTaskFaelligkeitsQuelle;
  context?: Record<string, unknown>;
}
export interface CreatePendingTaskResult {
  created: boolean;
  taskId?: string;
  error?: string;
}
export const datevPendingTaskService = {
  /**
   * Legt einen Pending Task an oder liefert einen existierenden aktiven Task
   * für dieselbe (customer, year, month, task_type)-Kombi zurück.
   *
   * Gibt { created: true, taskId } bei Neuanlage zurück,
   * { created: false, taskId } wenn bereits einer existierte,
   * { created: false, error } bei einem Fehler.
   */
  async createTask(input: CreatePendingTaskInput): Promise<CreatePendingTaskResult> {
    const {
      customerId,
      year,
      month,
      taskType,
      requiredDocuments = [],
      requiredNextMonthDocuments = [],
      faelligkeitsQuelle = 'datev_spiegel',
      context = {}
    } = input;
    if (!customerId || !year || !month || !taskType) {
      return {
        created: false,
        error: 'customerId, year, month und taskType sind Pflichtfelder'
      };
    }
    // Wartet der Task auf den DATEV-Spiegel, braucht er mindestens ein Dokument,
    // an dem die Erfüllung überhaupt festgemacht werden kann. Bei externer
    // Fälligkeitsquelle entfällt diese Pflicht, weil der Auslöser nicht im
    // Spiegel liegt (siehe Kopfkommentar).
    if (
      faelligkeitsQuelle === 'datev_spiegel' &&
      requiredDocuments.length === 0 &&
      requiredNextMonthDocuments.length === 0
    ) {
      return {
        created: false,
        error: 'Mindestens ein required document muss angegeben sein'
      };
    }
    try {
      // Idempotenz-Check: gibt es bereits einen aktiven Task?
      const { data: existing, error: existingErr } = await supabase
        .from('datev_pending_tasks')
        .select('id')
        .eq('customer_id', customerId)
        .eq('year', year)
        .eq('month', month)
        .eq('task_type', taskType)
        .in('status', ['pending', 'partially_fulfilled'])
        .maybeSingle();
      if (existingErr) {
        return { created: false, error: existingErr.message };
      }
      if (existing) {
        return { created: false, taskId: existing.id };
      }
      const { data: inserted, error: insertErr } = await supabase
        .from('datev_pending_tasks')
        .insert({
          customer_id: customerId,
          year,
          month,
          task_type: taskType,
          required_documents: requiredDocuments,
          required_next_month_documents: requiredNextMonthDocuments,
          fulfilled_documents: [],
          status: 'pending',
          check_count: 0,
          context
        })
        .select('id')
        .single();
      if (insertErr) {
        return { created: false, error: insertErr.message };
      }
      return { created: true, taskId: inserted.id };
    } catch (err) {
      return {
        created: false,
        error: err instanceof Error ? err.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Prüft, ob der Kunde tatsächlich bei DATEV autorisiert ist — d. h. ob für
   * seine Berater-/Mandantennummer eine gültige datev_auth-Zeile existiert
   * (app 'payrollreports', refresh_token gesetzt, refresh_token_expires_at in
   * der Zukunft). Spiegelt exakt die Autorisierungs-Logik der Edge Function
   * (fetchAuthorizedClientIds im datev-sync-Producer).
   *
   * Hintergrund: Nur für autorisierte Kunden landet das LOJO je im DATEV-
   * Spiegel. Für alle anderen liefe ein invoice_draft_autofill-Task
   * zwangsläufig in die Eskalation, weil sein required document nie erfüllt
   * wird. Diese Prüfung verhindert das Anlegen solcher toten Tasks.
   *
   * Defensiv: fehlen die Nummern oder tritt ein DB-Fehler auf, wird false
   * zurückgegeben (kein Task ist besser als ein toter Task). Der Aufrufer
   * behandelt false als „nicht anlegen".
   */
  async isCustomerDatevAuthorized(
    beraternummer: string | null | undefined,
    mandantennummer: string | null | undefined
  ): Promise<boolean> {
    const ber = (beraternummer ?? '').toString().trim();
    const man = (mandantennummer ?? '').toString().trim();
    if (ber.length === 0 || man.length === 0) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('datev_auth')
        .select('id')
        .eq('app', 'payrollreports')
        .eq('beraternummer', ber)
        .eq('mandantennummer', man)
        .not('refresh_token', 'is', null)
        .gt('refresh_token_expires_at', new Date().toISOString())
        .limit(1);
      if (error) {
        console.warn('[datevPendingTaskService] DATEV-Autorisierungs-Check fehlgeschlagen (non-blocking):', error.message);
        return false;
      }
      return Array.isArray(data) && data.length > 0;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[datevPendingTaskService] DATEV-Autorisierungs-Check fehlgeschlagen (non-blocking):', msg);
      return false;
    }
  }
};
